import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, FileDown, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { Money } from "@/components/kit";
import { LangToggle } from "@/components/layout/AppShell";
import { Logo } from "@/components/Logo";
import { errMsg } from "@/lib/format";
import { PortalLeaves } from "@/components/portal/PortalLeaves";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const KEY = "mohassib_emp_session";

const CodeForm = ({ info, token, onSession }) => {
  const { t } = useApp();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await axios.post(`${API}/employee/${token}/login`, { code }); sessionStorage.setItem(KEY + token, data.session); onSession(data.session); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="emp-portal-login">
      <p className="text-sm text-slate-600">{t("emp_portal_hello")} <b>{info.first_name}</b> — {info.company}</p>
      <label className="block text-sm font-medium text-slate-700">{t("emp_portal_code")}</label>
      <Input inputMode="numeric" maxLength={4} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-12 text-center text-2xl tracking-[0.5em]" data-testid="emp-portal-code" autoFocus />
      {err && <p className="text-sm text-red-600" data-testid="emp-portal-error">{err}</p>}
      <Button type="submit" disabled={busy || code.length !== 4} className="h-11 w-full" data-testid="emp-portal-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4 me-2" />}{t("emp_portal_enter")}</Button>
    </form>
  );
};

const Slips = ({ session, onExpired }) => {
  const { t } = useApp();
  const [me, setMe] = useState(null);
  const h = { headers: { "X-Employee-Session": session } };
  useEffect(() => { axios.get(`${API}/employee-portal/me`, h).then((r) => setMe(r.data)).catch(onExpired); }, [session]); // eslint-disable-line react-hooks/exhaustive-deps
  const get = async (path, name) => {
    try {
      const r = await axios.get(`${API}/employee-portal/${path}`, { ...h, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${name}.pdf`; a.click();
    } catch (e) { if (e.response?.status === 401) onExpired(); }
  };
  if (!me) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />;
  const L = (lang, path, name) => <Button size="sm" variant="outline" onClick={() => get(`${path}?lang=${lang}`, `${name}-${lang}`)} data-testid={`emp-dl-${name}-${lang}`}><FileDown className="h-3.5 w-3.5 me-1" />{lang === "fr" ? "FR" : "عربي"}</Button>;
  return (
    <div className="space-y-6" data-testid="emp-portal-space">
      <div><div className="eyebrow">{me.company}</div><div className="text-xl font-bold text-slate-900">{me.employee.first_name} {me.employee.last_name}</div><div className="text-xs text-slate-500">{[me.employee.code, me.employee.position].filter(Boolean).join(" · ")}</div></div>
      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">{t("emp_portal_slips")}</h2>
        {me.slips.length === 0 ? <p className="text-sm text-slate-400">{t("empty")}</p> : (
          <ul className="divide-y rounded-xl border bg-white">{me.slips.map((s) => (
            <li key={s.run_id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`emp-slip-${s.month}`}>
              <span className="num font-semibold">{s.month.slice(5)}/{s.month.slice(0, 4)}</span>
              <span className="text-xs text-slate-500">{t("pay_net")} <Money v={s.net} /></span>
              <span className="ms-auto flex gap-2">{L("fr", `slips/${s.run_id}/pdf`, `bulletin-${s.month}`)}{L("ar", `slips/${s.run_id}/pdf`, `bulletin-${s.month}`)}</span>
            </li>))}</ul>
        )}
      </section>
      <PortalLeaves session={session} onExpired={onExpired} />
      {me.years.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-slate-700">{t("emp_portal_certificate")}</h2>
          <ul className="divide-y rounded-xl border bg-white">{me.years.map((y) => (
            <li key={y} className="flex items-center gap-3 px-4 py-3" data-testid={`emp-cert-${y}`}><span className="num font-semibold">{y}</span>
              <span className="ms-auto flex gap-2">{L("fr", `certificate/${y}/pdf`, `attestation-${y}`)}{L("ar", `certificate/${y}/pdf`, `attestation-${y}`)}</span></li>))}</ul>
        </section>
      )}
    </div>
  );
};

export default function EmployeePortal() {
  const { token } = useParams();
  const { t } = useApp();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [session, setSession] = useState(sessionStorage.getItem(KEY + token));
  useEffect(() => { axios.get(`${API}/employee/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const logout = () => { sessionStorage.removeItem(KEY + token); setSession(null); };
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex items-center justify-between border-b bg-white px-6 py-3"><Logo size={30} /><div className="flex items-center gap-2">{session && info && <Button size="sm" variant="ghost" onClick={logout} aria-label={t("logout")} title={t("logout")} data-testid="emp-portal-logout"><LogOut className="h-4 w-4" /></Button>}<LangToggle /></div></header>
      <main className="mx-auto max-w-2xl p-4 sm:p-6">
        <div className="rounded-2xl border bg-white p-6 shadow-sm fade-up" data-testid="emp-portal-card">
          <h1 className="mb-4 text-lg font-bold text-slate-900">{t("emp_portal_title")}</h1>
          {err ? <p className="text-sm text-red-600" data-testid="emp-portal-invalid">{err}</p>
            : !info ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" />
              : session ? <Slips session={session} onExpired={logout} /> : <CodeForm info={info} token={token} onSession={setSession} />}
        </div>
        <p className="mt-4 text-center text-[11px] text-slate-400"><Lock className="me-1 inline h-3 w-3" />{t("emp_portal_private")}</p>
      </main>
    </div>
  );
}
